import { render } from 'preact';
import { registerSW } from 'virtual:pwa-register';
import { IdleApp } from './idle/IdleApp.tsx';
import { setUpdaterHandle, useShell } from './idle/shell.ts';
import { syncFakeClock } from './clock.ts';
import { apiRequest } from './game/sync.ts';
import { createNeedRefreshLatch } from './game/sw-signal.ts';
import { Updater, type HealthReply } from './game/updater.ts';
import { spriteRaw } from './ui/Icon.tsx';
import { OffNoteHost } from './ui/OffNote.tsx';
import './styles/base.css';

let updater: Updater | null = null;

/**
 * The plugin can announce a waiting worker before `onRegisteredSW` has built the updater -
 * iOS re-fetches `sw.js` during navigation, so the worker is often waiting first (D47).
 * The latch holds that signal and replays it the moment there is something to receive it.
 */
const needRefreshLatch = createNeedRefreshLatch();

/**
 * `registerType: 'prompt'` (vite.config.ts): a new worker installs and waits instead of
 * taking over on its own. `updateSW(true)` is the only thing that ever applies one, and
 * only `game/updater.ts` calls it, once it has decided the moment is safe.
 */
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh: () => needRefreshLatch.signal(),
  onRegisteredSW: (_url, registration) => {
    updater = new Updater({
      updateRegistration: () => void registration?.update(),
      hasWaiting: async () => {
        if (!('serviceWorker' in navigator)) return false;
        const reg = await navigator.serviceWorker.getRegistration();
        return !!reg?.waiting;
      },
      fetchHealth: async () => {
        const res = await apiRequest('/health', { cache: 'no-store' });
        if (!res || !res.ok) return null;
        return (await res.json()) as HealthReply;
      },
      // D280: the plugin only reloads on takeover when its own `waiting` event raised the
      // prompt. A worker found by the other two routes below took over and left the old page
      // on screen, so the reload is asked for here, once the new worker is in control.
      updateSW: async (reload) => {
        if (reload && 'serviceWorker' in navigator)
          navigator.serviceWorker.addEventListener('controllerchange', () => location.reload(), {
            once: true,
          });
        await updateSW(reload);
      },
      getPending: () => useShell.getState().pending,
      // D266: the old game's onboarding flag is not the idle game's; a stale draft of it in
      // storage must not hold every update back. The idle first run holds nothing unsaved.
      isOnboarding: () => false,
      appVersion: __APP_VERSION__,
      setTimer: (fn, ms) => setInterval(fn, ms),
      clearTimer: (h) => clearInterval(h as ReturnType<typeof setInterval>),
      onUpdateReady: () => useShell.getState().setUpdateReady(true),
      onServerVersion: (v) => useShell.getState().setServerVersion(v),
    });
    setUpdaterHandle(updater);
    needRefreshLatch.attach(() => updater?.needRefresh());
    updater.start();

    // Three ways to hear about a waiting worker, because on iOS only the last two are
    // reliable (D47): the plugin's own signal through the latch above; a worker that was
    // already waiting when this callback ran; and `updatefound`, for browsers where the
    // plugin's listener is attached too late to see the state change itself. A worker that
    // reaches `installed` while something is already controlling the page is a new version
    // waiting rather than the very first install.
    if (registration?.waiting) needRefreshLatch.signal();
    registration?.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          needRefreshLatch.signal();
        }
      });
    });

    // Resume triggers (D3 has the same pattern for presence): a visible tab asks again,
    // and only a return from hidden - not a bare focus while already visible - is treated
    // as a moment where an already-waiting update may be applied on its own.
    let wasHidden = document.visibilityState === 'hidden';
    document.addEventListener('visibilitychange', () => {
      const hiddenNow = document.visibilityState === 'hidden';
      if (!hiddenNow) updater?.onResume(wasHidden);
      wasHidden = hiddenNow;
    });
    window.addEventListener('focus', () => updater?.onResume(false));
    window.addEventListener('online', () => updater?.onResume(false));
  },
});
void syncFakeClock();

// The generated icon set (packages/art) is a hidden `<svg>` of `<symbol>`s. Inline it once so
// every `<use href="#icon-...">` in the app can find it. Bundled into the JS, not a separate
// asset, so it needs no entry in the PWA precache list.
document.body.insertAdjacentHTML('afterbegin', spriteRaw);

const root = document.getElementById('app');
if (root)
  render(
    <>
      {/* D240 and D244: the idle game. Archived classic worlds are listed in the idle
          settings and open read-only (D241, D262). */}
      <IdleApp />
      {/* D235: a press on any disabled control says why, from one listener. */}
      <OffNoteHost />
    </>,
    root,
  );
